import { type MapData, type MapEdge, type MapNode, isFundLike, isSelectable, shortName } from './types';

export interface Structure {
  k: number;
  id: string;
  name: string;
  members: Set<string>;
  funds: MapNode[];
}

export interface MapGraph {
  data: MapData;
  byId: Record<string, MapNode>;
  out: Record<string, MapEdge[]>;
  inc: Record<string, MapEdge[]>;
  edges: MapEdge[];
  component: Record<string, number>;
  structures: Structure[];
  admins: string[];
  /** cache de exposição final */
  memo: Record<string, Record<string, number>>;
}

/** Monta índices, componentes conexos ("estruturas") e administradores. */
export function buildGraph(data: MapData): MapGraph {
  const byId: Record<string, MapNode> = {};
  data.nos.forEach(n => { byId[n.id] = n; });
  const out: Record<string, MapEdge[]> = {}, inc: Record<string, MapEdge[]> = {};
  const edges = data.arestas.filter(e => byId[e.de] && byId[e.para]);
  edges.forEach(e => { (out[e.de] ||= []).push(e); (inc[e.para] ||= []).push(e); });

  const component: Record<string, number> = {};
  const structures: Structure[] = [];
  data.nos.forEach(n => {
    if (component[n.id] != null) return;
    const k = structures.length, stack = [n.id], members: string[] = [];
    component[n.id] = k;
    while (stack.length) {
      const x = stack.pop()!;
      members.push(x);
      [...(out[x] || []).map(e => e.para), ...(inc[x] || []).map(e => e.de)].forEach(y => {
        if (component[y] == null) { component[y] = k; stack.push(y); }
      });
    }
    const funds = members.map(m => byId[m]).filter(m => m.tipo === 'fundo').sort((a, b) => (b.pl || 0) - (a.pl || 0));
    structures.push({ k, id: '', name: '', members: new Set(members), funds });
  });
  structures.forEach(s => {
    s.id = s.funds.length ? s.funds[0].id : 'E' + s.k;
    s.name = s.funds.length <= 1
      ? (s.funds[0] ? shortName(s.funds[0]) : 'Outros')
      : shortName(s.funds[0]) + ', ' + shortName(s.funds[1]) + (s.funds.length > 2 ? ' +' + (s.funds.length - 2) : '');
  });
  structures.sort((a, b) => b.funds.length - a.funds.length || ((b.funds[0]?.pl || 0) - (a.funds[0]?.pl || 0)));
  const admins = [...new Set(data.nos.map(n => n.adm).filter((a): a is string => !!a))].sort();
  return { data, byId, out, inc, edges, component, structures, admins, memo: {} };
}

export const OTHERS = 'OUTROS';

/**
 * Exposição final (look-through): quanto do PL de `id` está em cada ativo/sociedade
 * no fim da cadeia, multiplicando as participações em fundos intermediários.
 * Dívidas recebidas entram negativas; o resíduo do PL vira "Outros líquidos".
 */
export function exposure(g: MapGraph, id: string): Record<string, number> {
  if (g.memo[id]) return g.memo[id];
  const n = g.byId[id], v: Record<string, number> = {};
  let sum = 0;
  g.memo[id] = v;
  (g.out[id] || []).forEach(e => {
    if ((e.tipo !== 'participacao' && e.tipo !== 'composicao') || typeof e.valor !== 'number') return;
    sum += e.valor;
    const y = g.byId[e.para];
    if (isFundLike(y) && y.pl) {
      const ey = exposure(g, y.id), f = e.valor / y.pl;
      for (const k in ey) v[k] = (v[k] || 0) + f * ey[k];
    } else v[y.id] = (v[y.id] || 0) + e.valor;
  });
  (g.inc[id] || []).forEach(e => {
    if (e.tipo === 'divida' && typeof e.valor === 'number') { v[e.de] = (v[e.de] || 0) - e.valor; sum -= e.valor; }
  });
  const resid = (n.pl || 0) - sum;
  if (n.pl && Math.abs(resid) > 0.5) v[OTHERS] = (v[OTHERS] || 0) + resid;
  return v;
}

export function categoryOf(g: MapGraph, key: string) {
  if (key === OTHERS) return 'outros';
  const n = g.byId[key];
  return n?.cat || (n?.tipo === 'spe' ? 'spe' : n?.tipo === 'divida' ? 'divida' : 'outros');
}

export const nodeName = (g: MapGraph, id: string) => (id === OTHERS ? 'Outros líquidos' : g.byId[id]?.t || id);

/** Exposição agregada por categoria de ativo. */
export function exposureByCategory(g: MapGraph, id: string) {
  const ex = exposure(g, id), byCat: Record<string, number> = {};
  for (const k in ex) { const c = categoryOf(g, k); byCat[c] = (byCat[c] || 0) + ex[k]; }
  return byCat;
}

/** Todos os ancestrais e descendentes de `id` (a "cadeia"). */
export function chain(g: MapGraph, id: string): Set<string> {
  const s = new Set([id]);
  const up = (x: string) => (g.inc[x] || []).forEach(e => { if (!s.has(e.de)) { s.add(e.de); up(e.de); } });
  const down = (x: string) => (g.out[x] || []).forEach(e => { if (!s.has(e.para)) { s.add(e.para); down(e.para); } });
  up(id); down(id);
  return s;
}

/** Fundos de um administrador, tudo o que eles detêm e as subclasses/cotistas que os compõem. */
export function adminReach(g: MapGraph, admin: string): Set<string> {
  const rel = new Set<string>();
  g.data.nos.filter(n => n.adm === admin && n.tipo === 'fundo').forEach(n => {
    rel.add(n.id);
    const down = (x: string) => (g.out[x] || []).forEach(e => { if (!rel.has(e.para)) { rel.add(e.para); down(e.para); } });
    const subs = (x: string) => (g.inc[x] || []).forEach(e => { if (e.tipo === 'composicao' || e.tipo === 'cotistas') { rel.add(e.de); subs(e.de); } });
    down(n.id); subs(n.id);
  });
  return rel;
}

/** Entidades citadas por uma nota do fechamento (em nós ou arestas). */
export function entitiesOfNote(g: MapGraph, k: number): string[] {
  const s = new Set<string>();
  g.data.nos.forEach(n => { if ((n.notas || []).includes(k) && isSelectable(n)) s.add(n.id); });
  g.edges.forEach(e => {
    if (e.nota !== k) return;
    if (isSelectable(g.byId[e.de])) s.add(e.de);
    if (isSelectable(g.byId[e.para])) s.add(e.para);
  });
  return [...s];
}

export const noteKey = (data: MapData, k: number) => `${data.dataBase || ''}#${k}`;

/** Texto pesquisável de um nó (nome, apelido, subtítulo, ficha, itens). */
export const searchableText = (n: MapNode) =>
  `${n.t} ${n.curto || ''} ${n.s || ''} ${(n.ficha || []).map(f => f[1]).join(' ')} ${(n.itens || []).map(i => i[0]).join(' ')}`;

/**
 * Completa dados vindos do banco: calcula o nível (1 = fundos e topos de estrutura,
 * 2 = investidas diretas, 3 = carteira) e o valor exibido quando faltam.
 */
export function normalizeMapData(data: MapData, money: (v: number) => string): MapData {
  const byId = new Map(data.nos.map(n => [n.id, n]));
  const parents = new Map<string, string[]>();
  data.arestas.forEach(e => {
    if (e.tipo === 'cotistas' || !byId.has(e.de) || !byId.has(e.para)) return;
    parents.set(e.para, [...(parents.get(e.para) ?? []), e.de]);
  });
  const level = new Map<string, number>();
  const visit = (id: string, path: Set<string>): number => {
    const known = level.get(id);
    if (known !== undefined) return known;
    const n = byId.get(id)!;
    if (n.nivel) { level.set(id, n.nivel); return n.nivel; }
    if (n.tipo === 'fundo' || n.tipo === 'subclasse') { level.set(id, 1); return 1; }
    path.add(id);
    const ps = (parents.get(id) ?? []).filter(p => !path.has(p));
    const l = ps.length ? Math.min(3, Math.min(...ps.map(p => visit(p, path))) + 1) : 1;
    path.delete(id);
    level.set(id, l);
    return l;
  };
  return {
    ...data,
    nos: data.nos.map(n => ({
      ...n,
      nivel: n.tipo === 'cotistas' ? n.nivel : visit(n.id, new Set()),
      v: n.v ?? (typeof n.pl === 'number' ? 'PL ' + money(n.pl) : undefined),
    })),
  };
}
